import { Injectable } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { Zone, ZoneMetadata } from "@common/ADAPT.Common.Model/methodology/zone";
import { ExternalDashboard } from "@common/ADAPT.Common.Model/organisation/external-dashboard";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { SystemLocation } from "@common/ADAPT.Common.Model/organisation/system-location";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { INavigationNode } from "@common/route/navigation-node.interface";
import { NavigationNodeBuilder } from "@common/route/navigation-node-builder";
import { NavigationUtilitiesService } from "@common/route/navigation-utilities.service";
import { CommonIntegratedArchitectureFrameworkAuthService } from "@org-common/lib/architecture/common-integrated-architecture-framework-auth.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { BullseyePageRoute } from "@org-common/lib/bullseye/bullseye-page/bullseye-page.component";
import { FeaturesService } from "@org-common/lib/features/features.service";
import { guidingPhilosophyRoute } from "@org-common/lib/guiding-philosophy/guiding-philosophy-page/guiding-philosophy-page.route";
import { organisationObjectivesPageRoute } from "@org-common/lib/objectives/objectives-page/objectives-page.route";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { OrganisationAuthService } from "@org-common/lib/organisation/organisation-auth.service";
import { rbGoalsRoute } from "@org-common/lib/rb-goals/rb-goals-page/rb-goals-page.route";
import { BaseOrgCommonNavigationHierarchyService } from "@org-common/lib/sidebar/base-org-common-navigation-hierarchy.service";
import { StrategicAnchorsPageRoute } from "@org-common/lib/strategic-anchors/strategic-anchors-page/strategic-anchors-page.component";
import { StrategyGoalsPageRoute } from "@org-common/lib/strategic-goals/strategic-goals-page/strategic-goals-page.component";
import { CompetitorAnalysisPageRoute } from "@org-common/lib/strategic-inputs/competitor-analysis-page/competitor-analysis-page.component";
import { StrategicInputsPageRoute } from "@org-common/lib/strategic-inputs/strategic-inputs-page/strategic-inputs-page.component";
import { StrategyAuthService } from "@org-common/lib/strategy/strategy-auth.service";
import { StrategyDashboardPageRoute } from "@org-common/lib/strategy-dashboard-page/strategy-dashboard-page.component";
import { analyseEngagementSurveyPageRoute, analyseResilientBusinessAssessmentSurveyPageRoute } from "@org-common/lib/survey/analyse-survey-page/analyse-survey-page.route";
import { manageEmployeeEngagementsPageRoute } from "@org-common/lib/survey/employee-engagement/manage-employee-engagements-page/manage-employee-engagements-page.route";
import { manageRBAssessmentsPageRoute } from "@org-common/lib/survey/resilient-business-assessment/manage-rb-assessments-page/manage-rb-assessments-page.route";
import { valuesConstitutionRoute } from "@org-common/lib/values-constitution/values-constitution-page/values-constitution-page.route";
import { guidanceMaterialsPageRoute } from "app/features/architecture/integrated-architecture/guidance-materials-page/guidance-materials-page.route";
import { rolesPageRoute } from "app/features/architecture/integrated-architecture/roles-page/roles-page.route";
import { culturalLeadershipFrameworkPageRoute } from "app/features/culture/cultural-leadership/clf-page/clf-page.route";
import { analyseCareerValuationDifferentialsPageRoute } from "app/features/people/career-valuation/analyse-cvt-differentials-page/analyse-cvt-differentials-page.route";
import { analyseCareerValuationPageRoute } from "app/features/people/career-valuation/analyse-cvt-page/analyse-cvt-page.route";
import { analyseCareerValuationTimelinePageRoute } from "app/features/people/career-valuation/analyse-cvt-timeline-page/analyse-cvt-timeline-page.route";
import { CareerValuationAuthService } from "app/features/people/career-valuation/career-valuation-auth.service";
import { culturalIndexOrbitPageRoute } from "app/features/people/cultural-index/ci-orbit-page/ci-orbit-page.route";
import { culturalIndexTimelinePageRoute } from "app/features/people/cultural-index/ci-timeline-page/ci-timeline-page.route";
import { CulturalIndexAuthService } from "app/features/people/cultural-index/cultural-index-auth.service";
import { employeeDirectoryPageRoute } from "app/features/people/directory/employee-directory-page/employee-directory-page.route";
import { analyseCatchupsPageRoute } from "app/features/people/peer-catchup/analyse-catchups-page/analyse-catchups-page.route";
import { stakeholderDirectoryPageRoute } from "app/features/stakeholders/stakeholder-directory-page/stakeholder-directory-page.route";
import { systemPageRoute } from "app/features/systemisation/system-page/system-page.route";
import { SystemisationService } from "app/features/systemisation/systemisation.service";
import { systemsPageRoute } from "app/features/systemisation/systems-page/systems-page.route";
import { organisationDashboardPageRoute } from "app/organisation/organisation-dashboard-page/organisation-dashboard-page.route";
import { organisationMapPageRoute } from "app/organisation/organisation-map-page/organisation-map-page.route";
import { lastValueFrom, merge } from "rxjs";

type ZoneChild = string | (() => Promise<INavigationNode>);

@Injectable()
export class OrganisationHierarchyNavigationService extends BaseOrgCommonNavigationHierarchyService {
    public static readonly HierarchyId = "ADAPT.Organisation.Navigation.Hierarchy";
    public static readonly IconClass = "fal fa-cubes";

    public id = OrganisationHierarchyNavigationService.HierarchyId;
    private childrenByZone: { [zone in Zone]: ZoneChild[] };

    public constructor(
        private navUtilitiesFactory: NavigationUtilitiesService,
        private orgService: OrganisationService,
        private authorisationFactory: AuthorisationService,
        authNotification: AuthorisationNotificationService,
        rxjsBreezeService: RxjsBreezeService,
        private systemisationService: SystemisationService,
        private featuresService: FeaturesService,
    ) {
        super(orgService, authNotification);

        this.childrenByZone = this.buildChildrenByZone();
        this.rebuildHierarchy$ = merge(
            rxjsBreezeService.entityTypeChangedInSave(Organisation),
            rxjsBreezeService.entityTypeChangedInSave(ExternalDashboard),
            rxjsBreezeService.entityTypeChanged(SystemLocation),
        );
    }

    protected async buildHierarchy() {
        const self = this;

        const orgBuilder = this.navUtilitiesFactory.nodeBuilderForControllerAndParams(organisationDashboardPageRoute.id)
            .setIconClass(OrganisationHierarchyNavigationService.IconClass)
            .keepChildrenInAddedOrder()
            .setHideIconInBreadcrumb(true)
            .promiseToSetTitle(this.promiseToGetOrganisationName());

        const hasExternalDashboardAccess = await this.authorisationFactory.promiseToGetHasAccess(OrganisationAuthService.ViewExternalDashboard);
        if (hasExternalDashboardAccess) {
            const externalDashboards = (await this.orgService.promiseToGetExternalDashboards()).map(
                (externalDashboard) => this.promiseToBuildExternalDashboardNode(externalDashboard, this.navUtilitiesFactory));
            orgBuilder.promiseToAddChildren(externalDashboards);
        }

        orgBuilder.promiseToAddChild(promiseToBuildOrganisationMapNode())
            .promiseToAddChildController(employeeDirectoryPageRoute.id)
            .promiseToAddChild(promiseToBuildOrganisationNode());

        await this.promiseToBuildZoneNodes(orgBuilder);

        return orgBuilder.promiseToBuild();

        function promiseToBuildOrganisationMapNode() {
            return self.navUtilitiesFactory.nodeBuilderForControllerAndParams(organisationMapPageRoute.id)
                .setCustomKeyValue("urlPriority", 0)
                .promiseToBuild();
        }

        function promiseToBuildOrganisationNode() {
            return self.navUtilitiesFactory.nodeBuilder()
                .keepChildrenInAddedOrder()
                .setTitle("Organisation")
                .setCustomKeyValue("expandOnLoad", true)
                .setCustomKeyValue("isHiddenInBreadcrumbs", true)
                .setCustomKeyValue("displayAsAccordionInHierarchy", true)
                .promiseToAddChildController(guidingPhilosophyRoute.id)
                .promiseToAddChildController(StrategyDashboardPageRoute.id)
                .promiseToAddChildController(rbGoalsRoute.id)
                .promiseToAddChildController(organisationObjectivesPageRoute.id)
                .promiseToAddChildController(BullseyePageRoute.id)
                .promiseToAddChildController(StrategyGoalsPageRoute.id)
                .promiseToAddChild(self.promiseToBuildStrategicInputsNode())
                .promiseToAddChildController(StrategicAnchorsPageRoute.id)
                .promiseToAddChild(self.promiseToBuildResilientBusinessAssessmentNode())
                .promiseToBuild();
        }
    }

    private async promiseToGetOrganisationName() {
        const organisation = await this.orgService.promiseToGetOrganisation();
        return organisation?.name ?? "";
    }

    @Autobind
    private promiseToBuildAnalyseCulturalIndex() {
        return this.navUtilitiesFactory.nodeBuilder()
            .ifAuthorised(CulturalIndexAuthService.OpenCulturalIndexAnalysis)
            .keepChildrenInAddedOrder()
            .setTitle("Cultural Index")
            .setCustomKeyValue("onClickGoToController", culturalIndexOrbitPageRoute.id)
            .promiseToAddChildController(culturalIndexOrbitPageRoute.id)
            .promiseToAddChildController(culturalIndexTimelinePageRoute.id)
            .promiseToBuild();
    }

    @Autobind
    private promiseToBuildAnalyseCareerValuation() {
        return this.navUtilitiesFactory.nodeBuilder()
            .ifAuthorised(CareerValuationAuthService.OpenCareerValuationAnalysis)
            .keepChildrenInAddedOrder()
            .setTitle("Career Valuation")
            .setCustomKeyValue("onClickGoToController", analyseCareerValuationPageRoute.id)
            .promiseToAddChildController(analyseCareerValuationPageRoute.id)
            .promiseToAddChildController(analyseCareerValuationDifferentialsPageRoute.id)
            .promiseToAddChildController(analyseCareerValuationTimelinePageRoute.id)
            .promiseToBuild();
    }

    @Autobind
    private promiseToBuildEngagementNode() {
        return this.navUtilitiesFactory.nodeBuilderForControllerAndParams(manageEmployeeEngagementsPageRoute.id)
            .keepChildrenInAddedOrder()
            .promiseToAddChildController(analyseEngagementSurveyPageRoute.id)
            .promiseToBuild();
    }

    @Autobind
    private promiseToBuildResilientBusinessAssessmentNode() {
        return this.navUtilitiesFactory.nodeBuilderForControllerAndParams(manageRBAssessmentsPageRoute.id)
            .promiseToAddChildController(analyseResilientBusinessAssessmentSurveyPageRoute.id)
            .promiseToBuild();
    }

    @Autobind
    private async promiseToBuildStrategicInputsNode() {
        // Inputs node will do redirect to a child controller.
        // Permission is only checked when child controller is added. Parent node is redirecting to child controller.
        // So if you don't have permission, parent node is still created and redirecting to child controller.
        // Check for permission first before creating the parent node - both children have the same permission
        const canReadStrategicInputs = await this.authorisationFactory.promiseToGetHasAccess(StrategyAuthService.ReadStrategicInputs);
        if (canReadStrategicInputs) {
            return this.navUtilitiesFactory.nodeBuilder()
                .keepChildrenInAddedOrder()
                .setTitle("Strategic Inputs")
                .setCustomKeyValue("onClickGoToController", StrategicInputsPageRoute.id)
                .promiseToAddChildController(StrategicInputsPageRoute.id)
                .promiseToAddChildController(CompetitorAnalysisPageRoute.id)
                .promiseToBuild();
        }

        return Promise.reject();
    }

    @Autobind
    private async promiseToBuildArchitectureComponents() {
        return this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(guidanceMaterialsPageRoute.id)
            .promiseToBuild();
    }

    private async promiseToBuildZoneNodes(builder: NavigationNodeBuilder) {
        // A permission refresh from a previous save can include a newly toggled, unsaved feature.
        // The server cannot authorise its location queries until that feature is persisted.
        const hasSystemPermissions = await this.authorisationFactory.promiseToGetHasAccess(CommonIntegratedArchitectureFrameworkAuthService.ReadSystem)
            && this.featuresService.checkIfFeatureActiveAndSaved(FeatureName.ArchitectureSystem);
        if (hasSystemPermissions && ZoneMetadata.VisibleZones.some((zone) => this.childrenByZone[zone].length > 0 || (zone === Zone.EconomicEngine))) {
            await lastValueFrom(this.systemisationService.primeSystemLocationsForZones());
        }

        for (const zone of ZoneMetadata.VisibleZones) {
            if (this.childrenByZone[zone].length > 0 || (zone === Zone.EconomicEngine)) {
                // cannot have this await within the promiseToBuildZoneNode function (making it async)
                // as too many async-await within the call stack will mess up rejection handling (i.e. rejection not because caught by promiseToAddChild)
                const systemLocations = hasSystemPermissions
                    ? await lastValueFrom(this.systemisationService.getSystemLocationsForZone(zone))
                    : [];
                builder.promiseToAddChild(this.promiseToBuildZoneNode(zone, systemLocations));
            }
        }
    }

    public promiseToBuildZoneNode(zone: Zone, systemLocations?: SystemLocation[]) {
        const self = this;

        const builder = this.navUtilitiesFactory.nodeBuilder()
            .keepChildrenInAddedOrder()
            .setTitle(ZoneMetadata.Name[zone])
            .setCustomKeyValue("expandOnLoad", true)
            .setCustomKeyValue("isHiddenInBreadcrumbs", true)
            .setCustomKeyValue("displayAsAccordionInHierarchy", true);

        if (systemLocations) {
            for (const systemLocation of systemLocations) {
                builder.promiseToAddChild(this.navUtilitiesFactory
                    .nodeBuilderForControllerAndParams(systemPageRoute.id, { systemEntityId: systemLocation.systemEntityId })
                    .setTitle(systemLocation.system.name)
                    .promiseToBuild());
            }
        }

        const childrenControllerIds = this.childrenByZone[zone];
        for (const controllerId of childrenControllerIds) {
            builder.promiseToAddChild(buildZoneSpecificChildNode(controllerId));
        }

        return builder.promiseToBuild();

        async function buildZoneSpecificChildNode(controllerId: ZoneChild) {
            if (typeof controllerId === "function") {
                return controllerId();
            } else {
                return self.navUtilitiesFactory.nodeBuilderForControllerAndParams(controllerId)
                    .ifAuthorised()
                    .promiseToBuild();
            }
        }
    }

    private buildChildrenByZone() {
        return {
            [Zone.EconomicEngine]: [],
            [Zone.HealthyCulture]: [
                valuesConstitutionRoute.id,
                this.promiseToBuildAnalyseCulturalIndex,
                this.promiseToBuildAnalyseCareerValuation,
                culturalLeadershipFrameworkPageRoute.id,
                analyseCatchupsPageRoute.id,
                this.promiseToBuildEngagementNode,
                stakeholderDirectoryPageRoute.id,
            ],
            [Zone.FinanceAndOwnership]: [],
            [Zone.OrganisationDesign]: [
                systemsPageRoute.id,
                rolesPageRoute.id,
                this.promiseToBuildArchitectureComponents,
            ],
            [Zone.ResearchAndDevelopment]: [],
        };
    }
}
